import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import api, { fmtInt, fmtQty, fmtDate, daysUntil } from '../../utils/api';

export default function WarehouseDashboardPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const he = i18n.language === 'he';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get('/warehouse-dashboard');
      setData(r.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;
  if (!data) return <div className="empty-state">{t('common.noData')}</div>;

  const { pullOrdersPending, qrPending, siArriving, cellcomOrders, stock, externalWarehouse, cellcomByStatus } = data;

  const greet = he ? `שלום ${user?.full_name || user?.username}` : `Hello ${user?.full_name || user?.username}`;

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{greet}</h1>
      <div style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
        {he ? 'דשבורד מנהל מחסן — פעולות פתוחות ומלאי' : 'Warehouse manager dashboard — open actions & stock'}
      </div>

      {/* ═══ Action items ═══ */}
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#ea580c', fontWeight: 700, margin: '0 0 10px', borderBottom: '2px solid #fb923c', paddingBottom: 6 }}>
        {he ? '📋 פעולות פתוחות' : '📋 Open actions'}
      </div>
      <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBottom: 24 }}>
        <ActionCard
          icon="🚚"
          label={he ? 'משיכות ממחסן מרכזי' : 'Pull orders pending'}
          value={pullOrdersPending.count}
          sub={he
            ? `${pullOrdersPending.totalPallets} משטחים · ${fmtInt(pullOrdersPending.totalUnits)} יח׳`
            : `${pullOrdersPending.totalPallets} pallets · ${fmtInt(pullOrdersPending.totalUnits)} units`}
          urgent={pullOrdersPending.count > 0}
          onClick={() => navigate('/warehouse')}
        />
        <ActionCard
          icon="🔲"
          label={he ? 'QR Serials לטיפול' : 'QR Serials pending'}
          value={qrPending.count}
          sub={he
            ? `${qrPending.skus} מק"טים`
            : `${qrPending.skus} SKUs`}
          urgent={qrPending.count > 0}
          onClick={() => navigate('/warehouse')}
        />
        <ActionCard
          icon="📋"
          label={he ? 'הזמנות Cellcom' : 'Cellcom orders'}
          value={cellcomOrders.count}
          sub={he
            ? `${fmtInt(cellcomOrders.totalUnits)} יח׳ · ${fmtInt(cellcomOrders.totalRevenue)}`
            : `${fmtInt(cellcomOrders.totalUnits)} units · ${fmtInt(cellcomOrders.totalRevenue)}`}
          urgent={cellcomOrders.actionableCount > 0}
          onClick={() => navigate('/cellcom-orders')}
        />
        <ActionCard
          icon="🚢"
          label={he ? 'SI מגיעים תוך 30 יום' : 'SI arriving in 30 days'}
          value={siArriving.count}
          sub={he
            ? `${fmtInt(siArriving.totalUnits)} יח׳ · ${fmtInt(siArriving.totalValue)}`
            : `${fmtInt(siArriving.totalUnits)} units · ${fmtInt(siArriving.totalValue)}`}
          onClick={() => navigate('/logistics')}
        />
      </div>

      {/* ═══ Inventory snapshot ═══ */}
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#0071e3', fontWeight: 700, margin: '0 0 10px', borderBottom: '2px solid #3b82f6', paddingBottom: 6 }}>
        {he ? '📊 מלאי' : '📊 Inventory'}
      </div>
      <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: 24 }}>
        <InfoCard
          icon="🏬"
          label={he ? 'מלאי S&T' : 'S&T Stock'}
          value={fmtQty(stock.stUnits)}
          sub={he ? `${stock.stSkus} מק"טים · יח׳` : `${stock.stSkus} SKUs · units`}
        />
        <InfoCard
          icon="🏭"
          label={he ? 'מלאי Shager' : 'Shager Stock'}
          value={fmtQty(stock.shagerUnits)}
          sub={he ? `${stock.shagerSkus} מק"טים · יח׳` : `${stock.shagerSkus} SKUs · units`}
        />
        <InfoCard
          icon="📦"
          label={he ? 'סה"כ מלאי' : 'Total Stock'}
          value={fmtQty(stock.totalUnits)}
          sub={he ? "יח'" : 'units'}
          highlight
        />
        <InfoCard
          icon="📦"
          label={he ? 'מחסן חיצוני' : 'External Warehouse'}
          value={fmtQty(externalWarehouse.units)}
          sub={he
            ? `${externalWarehouse.pallets} משטחים · ${fmtInt(externalWarehouse.cartons)} קרטונים`
            : `${externalWarehouse.pallets} pallets · ${fmtInt(externalWarehouse.cartons)} cartons`}
          onClick={() => navigate('/warehouse')}
        />
      </div>

      {/* ═══ Pull orders detail ═══ */}
      {pullOrdersPending.list.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
            🚚 {he ? 'משיכות ממתינות' : 'Pending pull orders'}
          </h3>
          <div className="table-wrap"><table>
            <thead><tr>
              <th>{he ? 'מס׳ הזמנה' : 'Order #'}</th>
              <th>{he ? 'תאריך' : 'Date'}</th>
              <th>{he ? 'שורות' : 'Lines'}</th>
              <th>{he ? 'משטחים' : 'Pallets'}</th>
              <th>{he ? 'יח׳ משוער' : 'Est. units'}</th>
              <th>{he ? 'הערות' : 'Notes'}</th>
            </tr></thead>
            <tbody>
              {pullOrdersPending.list.map(p => (
                <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/warehouse')}>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>{p.pullOrderNo}</td>
                  <td>{fmtDate(p.createdAt)}</td>
                  <td>{p.lineCount}</td>
                  <td>{p.totalPallets}</td>
                  <td>{fmtInt(p.totalUnits)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {/* ═══ Cellcom orders detail (all non-cancelled, sorted by status priority) ═══ */}
      {cellcomOrders.list.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
            📋 {he ? 'הזמנות Cellcom' : 'Cellcom orders'}
          </h3>
          <div className="table-wrap"><table>
            <thead><tr>
              <th>{he ? 'מס׳ הזמנה' : 'Order #'}</th>
              <th>{he ? 'סטטוס' : 'Status'}</th>
              <th>{he ? 'שינוע' : 'Transport'}</th>
              <th>{he ? 'נוצר' : 'Created'}</th>
              <th>{he ? 'מק"טים' : 'SKUs'}</th>
              <th>{he ? "יח'" : 'Units'}</th>
              <th>{he ? 'הכנסה' : 'Revenue'}</th>
            </tr></thead>
            <tbody>
              {cellcomOrders.list.map(c => {
                const statusBg = c.status === 'pending' ? '#fef3c7'
                  : c.status === 'ready' ? '#dcfce7'
                  : c.status === 'picked' ? '#dbeafe'
                  : c.status === 'shipped' ? '#e0e7ff'
                  : '#f3f4f6';
                const statusColor = c.status === 'pending' ? '#a16207'
                  : c.status === 'ready' ? '#16a34a'
                  : c.status === 'picked' ? '#1e40af'
                  : c.status === 'shipped' ? '#4338ca'
                  : '#475569';
                return (
                  <tr key={c.orderNumber} style={{ cursor: 'pointer' }} onClick={() => navigate('/cellcom-orders')}>
                    <td style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>#{c.orderNumber}</td>
                    <td>
                      <span style={{
                        background: statusBg, color: statusColor,
                        padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                      }}>{c.status}</span>
                    </td>
                    <td>{c.transport === 'AIR' ? '✈ Air' : '🚢 Ocean'}</td>
                    <td>{fmtDate(c.createdAt)}</td>
                    <td>{c.skuCount}</td>
                    <td>{fmtInt(c.units)}</td>
                    <td>{fmtInt(c.revenue)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}

      {/* ═══ SI arriving detail ═══ */}
      {siArriving.list.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
            🚢 {he ? 'הזמנות SI מגיעות' : 'SI orders arriving'}
          </h3>
          <div className="table-wrap"><table>
            <thead><tr>
              <th>{he ? 'הזמנה' : 'Order'}</th>
              <th>{he ? 'תאריך הזמנה' : 'Order Date'}</th>
              <th>{he ? 'הגעה צפויה' : 'ETA'}</th>
              <th>{he ? 'ימים' : 'Days'}</th>
              <th>{he ? 'שינוע' : 'Transport'}</th>
              <th>{he ? 'מק"טים' : 'SKUs'}</th>
              <th>{he ? "יח'" : 'Units'}</th>
            </tr></thead>
            <tbody>
              {siArriving.list.map(s => {
                const days = s.arrivalDate ? daysUntil(s.arrivalDate) : null;
                const overdue = days !== null && days < 0;
                return (
                  <tr key={s.orderNo} style={{ cursor: 'pointer' }} onClick={() => navigate('/logistics')}>
                    <td style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>{s.orderNo}</td>
                    <td>{fmtDate(s.orderDate)}</td>
                    <td>{s.arrivalDate ? fmtDate(s.arrivalDate) : '—'}</td>
                    <td style={{ color: overdue ? '#dc2626' : days !== null && days <= 7 ? '#ea580c' : 'var(--text)' }}>
                      {days === null ? '—' : days < 0 ? `${Math.abs(days)} ${he ? 'באיחור' : 'overdue'}` : `${days}${he ? '' : ' d'}`}
                    </td>
                    <td>{s.transport === 'AIR' ? '✈' : '🚢'} {s.transport || ''}</td>
                    <td>{s.skuCount}</td>
                    <td>{fmtInt(s.units)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}

function ActionCard({ icon, label, value, sub, urgent, onClick }) {
  const isZero = !value || value === 0;
  return (
    <div
      className="summary-card"
      style={{
        cursor: onClick ? 'pointer' : 'default',
        border: urgent && !isZero ? '2px solid #ea580c' : '1px solid var(--border)',
        background: urgent && !isZero ? '#fff7ed' : '#fff',
        borderRadius: 8,
        padding: 16,
      }}
      onClick={onClick}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: urgent && !isZero ? '#ea580c' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
          {label}
        </span>
        <span style={{ fontSize: 22 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: isZero ? 'var(--text-muted)' : urgent ? '#ea580c' : 'var(--text)', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>{sub}</div>
    </div>
  );
}

function InfoCard({ icon, label, value, sub, highlight, onClick }) {
  return (
    <div
      className="summary-card"
      style={{
        cursor: onClick ? 'pointer' : 'default',
        border: '1px solid var(--border)',
        background: highlight ? '#dbeafe' : '#fff',
        borderRadius: 8,
        padding: 16,
      }}
      onClick={onClick}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
          {label}
        </span>
        <span style={{ fontSize: 22 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: highlight ? '#0071e3' : 'var(--text)', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>{sub}</div>
    </div>
  );
}
